/* eslint-disable no-underscore-dangle */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Badge, Button, Form, Spinner,
} from 'react-bootstrap';
import _ from 'lodash';
import { get } from '../../../Services/API';
import { formatDateTime } from '../../../Utils/timezone';

// Loose validation: PH booking refs are alphanumeric with underscores
// (e.g. PU_DASH_1234567). The real authority is whether the /bookings
// lookup finds matches — this regex only blocks obvious junk
// (single chars, spaces, etc.) from triggering a network call.
const BOOKING_REF_PATTERN = /^[A-Za-z0-9_-]{2,}$/;

// "Looks like a PU_ booking reference?" — fast-path detector. Anything
// matching `^PU_…` or fitting the loose ref pattern routes through the
// exact-match `booking_reference` filter (preserves the v1 flow).
// Anything else (names with spaces, emails, phones) routes through the
// new free-text `q` filter on the backend.
const PU_PREFIX_PATTERN = /^PU_/i;

function formatMoney(amount, currency) {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  const n = Number(amount);
  if (currency === 'HUF') return `${Math.round(n).toLocaleString('en-US')} Ft`;
  return `€${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Bootstrap-style color mapping for booking status badges. Mirrors the
// scheme used in OrderDetailsModal / Orders pages.
function statusBadgeVariant(status) {
  switch (status) {
    case 'Confirmed': return 'success';
    case 'Completed': return 'success';
    case 'Pending': return 'warning';
    case 'Request': return 'warning';
    case 'Pending Ingestion': return 'warning';
    case 'Cancelled': return 'danger';
    case 'Driver No-Show': return 'danger';
    case 'Passenger No-Show': return 'danger';
    case 'Ingestion Failed': return 'danger';
    default: return 'secondary';
  }
}

/**
 * Shared booking-reference search input used by:
 *   - LinkToBookingModal (link an unmatched payment to a booking)
 *   - RecordPaymentModal (counterparty=booking picker, line allocation pickers)
 *
 * v2 behavior — click to attach (not auto-resolve):
 *   1. User types a booking ref
 *   2. Debounced search returns matches (up to LIMIT)
 *   3. Each match is shown as a clickable row with status + name + total
 *   4. `onResolve(booking, { reason: 'match' })` fires ONLY when user clicks
 *   5. × on the selection pill clears (`reason: 'cleared'`)
 *
 * The caller still controls the input text via `value` + `onChange` so it
 * can be reset between modal opens. The component owns the results list,
 * the selection pill, and the spinner / "no matches" / "error" states.
 */
function BookingReferenceSearch({
  value,
  onChange,
  onResolve,
  // Optional: enables checkbox multi-select in the results list. Called with an
  // array of the ticked bookings so the caller can attach them all at once.
  onResolveMany = null,
  selected, // optional: caller-controlled selection (Booking | null)
  placeholder = 'PU_DASH_… or name / email / phone',
  autoFocus = false,
  size,
  controlId,
  label,
  disabled = false,
  // Layer-3 defense: when the parent payment has an operator/partner
  // counterparty, scope the dropdown to bookings that actually belong
  // to that counterparty. Without this scope an admin can pick a
  // booking belonging to a different operator and the line silently
  // misattributes money (prod bug `6a191393`). The `/bookings` endpoint
  // already accepts `operators: string[]` and `partners: string[]`
  // filters so this is a pure UI tightening — no backend change.
  scopeOperatorId,
  scopePartnerId,
}) {
  const [searching, setSearching] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  // Ticked booking _ids for multi-select (only when onResolveMany is provided).
  const [multiSelected, setMultiSelected] = useState([]);

  // Internal selection — used when caller doesn't pass `selected`.
  const [internalSelected, setInternalSelected] = useState(null);
  const effectiveSelected = selected !== undefined ? selected : internalSelected;

  // Race protection: drop slow earlier responses when a newer search lands.
  const lookupSeqRef = useRef(0);

  // Stash onResolve in a ref so changes to its identity don't tear down
  // the debounced lookup.
  const onResolveRef = useRef(onResolve);
  useEffect(() => { onResolveRef.current = onResolve; }, [onResolve]);

  const runLookup = useCallback(async (ref) => {
    const trimmed = (ref || '').trim();
    if (!trimmed) {
      setResults([]);
      setLookupError('');
      setSearching(false);
      setHasSearched(false);
      return;
    }

    // Loosened guard: at least 2 non-whitespace characters. Names have
    // spaces (e.g. "Mark Smith"), so we no longer require the
    // alphanumeric+underscore pattern up-front — only that the operator
    // has typed enough to make the search worth running.
    if (trimmed.length < 2) {
      setResults([]);
      setLookupError('Enter at least 2 characters');
      setSearching(false);
      setHasSearched(true);
      return;
    }

    // Route by input shape:
    //   "PU_…" or alphanumeric-only refs → exact-match booking_reference
    //     filter (preserves the operator-paste-a-ref flow + the v1 tests
    //     that assert `filters: { booking_reference: ref }`).
    //   anything else (names, emails, phones) → free-text `q` which the
    //     backend matches against booking_reference OR full_name OR
    //     email_address OR phone_number, case-insensitive.
    const looksLikeRef = PU_PREFIX_PATTERN.test(trimmed)
      || BOOKING_REF_PATTERN.test(trimmed);
    const lookupFilters = looksLikeRef
      ? { booking_reference: trimmed }
      : { q: trimmed };

    // Apply the counterparty scope when present. `operators` / `partners`
    // are array filters server-side (BookingsService.filterBookings) —
    // pass a single-element array so the existing query path matches.
    if (scopeOperatorId) lookupFilters.operators = [scopeOperatorId];
    if (scopePartnerId) lookupFilters.partners = [scopePartnerId];

    const mySeq = lookupSeqRef.current + 1;
    lookupSeqRef.current = mySeq;
    setSearching(true);
    setLookupError('');
    try {
      const response = await get('/bookings', { filters: lookupFilters });
      if (mySeq !== lookupSeqRef.current) return;
      const docs = response.data?.data?.documents || [];
      setResults(docs.slice(0, 10));
      setHasSearched(true);
    } catch (err) {
      if (mySeq !== lookupSeqRef.current) return;
      setLookupError(err.response?.data?.message || err.message || 'Lookup failed');
      setResults([]);
      setHasSearched(true);
    } finally {
      if (mySeq === lookupSeqRef.current) setSearching(false);
    }
  }, [scopeOperatorId, scopePartnerId]);

  const debouncedLookup = useMemo(
    () => _.debounce((ref) => { runLookup(ref); }, 300),
    [runLookup],
  );

  useEffect(() => () => debouncedLookup.cancel(), [debouncedLookup]);

  const handleChange = (e) => {
    const v = e.target.value;
    if (onChange) onChange(v);
    setLookupError('');
    if (!v.trim()) {
      debouncedLookup.cancel();
      setSearching(false);
      setResults([]);
      setHasSearched(false);
      return;
    }
    setSearching(true);
    debouncedLookup(v);
  };

  const handleSelect = (booking) => {
    if (selected === undefined) setInternalSelected(booking);
    setResults([]);
    setHasSearched(false);
    setMultiSelected([]);
    if (onChange) onChange('');
    const cb = onResolveRef.current;
    if (cb) cb(booking, { reason: 'match' });
  };

  const toggleMulti = (id) => setMultiSelected((prev) => (
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
  ));

  // Attach every ticked booking at once, then clear the results.
  const handleAddMany = () => {
    const picked = results.filter((b) => multiSelected.includes(b._id));
    if (picked.length === 0) return;
    setResults([]);
    setHasSearched(false);
    setMultiSelected([]);
    if (onChange) onChange('');
    if (onResolveMany) onResolveMany(picked);
  };

  const handleClear = () => {
    if (selected === undefined) setInternalSelected(null);
    if (onChange) onChange('');
    setResults([]);
    setHasSearched(false);
    setLookupError('');
    const cb = onResolveRef.current;
    if (cb) cb(null, { reason: 'cleared' });
  };

  const input = (
    <Form.Control
      type="text"
      size={size}
      placeholder={placeholder}
      value={value || ''}
      onChange={handleChange}
      autoFocus={autoFocus}
      disabled={disabled || !!effectiveSelected}
    />
  );

  return (
    <div>
      {effectiveSelected && (
        <div className="d-flex align-items-center mb-2 p-2 border rounded bg-light">
          <div className="flex-grow-1 small">
            <strong>{effectiveSelected.booking_reference || effectiveSelected._id}</strong>
            {effectiveSelected.full_name && (
              <>
                {' — '}
                {effectiveSelected.full_name}
              </>
            )}
            {effectiveSelected.status && (
              <Badge bg={statusBadgeVariant(effectiveSelected.status)} className="ms-2">
                {effectiveSelected.status}
              </Badge>
            )}
          </div>
          <Button
            variant="link"
            size="sm"
            onClick={handleClear}
            className="text-muted p-0 ms-2"
            aria-label="Clear selection"
            type="button"
          >
            <i className="fa fa-times" />
          </Button>
        </div>
      )}

      {!effectiveSelected && (
        label ? (
          <Form.Group controlId={controlId}>
            <Form.Label>{label}</Form.Label>
            {input}
          </Form.Group>
        ) : input
      )}

      {!effectiveSelected && searching && (
        <div className="d-flex align-items-center small text-muted mt-1">
          <Spinner animation="border" size="sm" className="me-2" />
          Searching…
        </div>
      )}

      {!effectiveSelected && !searching && lookupError && (
        <div className="small text-warning mt-1" role="alert">
          {lookupError}
        </div>
      )}

      {!effectiveSelected && !searching && !lookupError && results.length > 0 && (
        <div className="mt-2 border rounded" style={{ maxHeight: 320, overflowY: 'auto' }}>
          <div className="small text-muted px-2 py-1 border-bottom d-flex align-items-center justify-content-between gap-2">
            <span>
              {results.length === 1
                ? '1 match — click to attach'
                : `${results.length} matches — click one${onResolveMany ? ', or tick several' : ''} to attach`}
            </span>
            {onResolveMany && multiSelected.length > 0 && (
              <Button size="sm" variant="primary" className="py-0 text-nowrap" onClick={handleAddMany} type="button">
                {`Add ${multiSelected.length} selected`}
              </Button>
            )}
          </div>
          {results.map((booking) => (
            <div
              key={booking._id}
              className="d-flex align-items-center border-bottom px-2 py-2 gap-2"
            >
              {onResolveMany && (
                <Form.Check
                  type="checkbox"
                  checked={multiSelected.includes(booking._id)}
                  onChange={() => toggleMulti(booking._id)}
                  aria-label={`Select ${booking.booking_reference || booking._id}`}
                />
              )}
              <button
                type="button"
                className="w-100 text-start border-0 bg-transparent p-0"
                onClick={() => handleSelect(booking)}
                style={{ cursor: 'pointer' }}
              >
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <div className="small">
                      <code className="me-2">{booking.booking_reference || booking._id}</code>
                      {booking.status && (
                        <Badge bg={statusBadgeVariant(booking.status)} className="me-1">
                          {booking.status}
                        </Badge>
                      )}
                    </div>
                    <div className="small mt-1">
                      {booking.full_name || '—'}
                    </div>
                    <div className="small text-muted">
                      {booking.date_datetime
                        ? formatDateTime(booking.date_datetime)
                        : (booking.date || '—')}
                    </div>
                  </div>
                  <div className="small text-end text-muted ms-2">
                    {formatMoney(booking.total_price ?? booking.price, booking.currency)}
                  </div>
                </div>
              </button>
            </div>
          ))}
        </div>
      )}

      {!effectiveSelected && !searching && !lookupError && hasSearched && results.length === 0 && (
        <div className="small text-muted mt-1">
          {(() => {
            if (scopeOperatorId) return 'No bookings for this operator matching that input.';
            if (scopePartnerId) return 'No bookings for this partner matching that input.';
            return 'No matching bookings.';
          })()}
        </div>
      )}
    </div>
  );
}

export default BookingReferenceSearch;
